import { ButtonType, EpicIcons } from "@constellation-academy/epic-ui";
import {
	DefaultNumberFieldComponent,
	DefaultTextFieldComponent,
	ValidatedField,
} from "@constellation-academy/framework-react-components";
import { useFormik } from "formik";
import moment from "moment/moment";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Suspense, useRef } from "react";
import { useFragment, useMutation } from "react-relay";
import { Button } from "@components/button";
import { useTranslation } from "@i18n/hooks";
import { type discountActionForm_DiscountActionFragment$key } from "@relay/discountActionForm_DiscountActionFragment.graphql";
import {
	type discountActionForm_EditDiscountActionMutation,
	type UsageLimitedInput,
} from "@relay/discountActionForm_EditDiscountActionMutation.graphql";
import { discountActionForm_ReactivateDiscountActionMutation } from "@relay/discountActionForm_ReactivateDiscountActionMutation.graphql";
import { DiscountTypeKindInput } from "@relay/discountActionsScreen_Query.graphql";
import {
	DISCOUNT_ACTION_FRAGMENT,
	EDIT_DISCOUNT_ACTION_MUTATION,
	REACTIVATE_DISCOUNT_ACTION_MUTATION,
} from "./discount-action-form.graphql";
import { discountActionFormValidationSchema } from "./discount-action-form.schema";
import {
	type DiscountActionFormProps,
	type DiscountActionFormState,
} from "./discount-action-form.types";
import { DefaultSwitchComponent } from "../../../../components/DefaultTextInput";
import { DiscountTypeInput } from "../discount-type-input";
import { UsageLimitationInput } from "../UsageLimitationInput";

export const DiscountActionForm = ({ discountActionFragmentRef }: DiscountActionFormProps) => {
	const { t } = useTranslation("common");
	const toast = useRef<Toast>(null);

	const discountAction = useFragment<discountActionForm_DiscountActionFragment$key>(
		DISCOUNT_ACTION_FRAGMENT,
		discountActionFragmentRef,
	);

	const [editPercentageDiscountAction, isEditingPercentageDiscountAction] =
		useMutation<discountActionForm_EditDiscountActionMutation>(EDIT_DISCOUNT_ACTION_MUTATION);

	const [reactivateDiscountAction, isReactivatingDiscountAction] =
		useMutation<discountActionForm_ReactivateDiscountActionMutation>(
			REACTIVATE_DISCOUNT_ACTION_MUTATION,
		);

	const { isArchived, kind } = discountAction.archiveInfo;
	const oldUsage = discountAction.usageLimitation;
	const formik = useFormik<DiscountActionFormState>({
		initialValues: {
			id: discountAction.id,
			title: discountAction.title,
			isReductible: discountAction.isReductible,
			discountType: discountAction.discountType.kind,
			percentage: discountAction.percent || 0,
			validUntil: discountAction.validUntil.dateTime,
			usageLimitation:
				discountAction.usageLimitation.kind === "UsageLimited"
					? {
							maxAmountOfUsages:
								discountAction.usageLimitation.maxAmountOfUsages?.max,
							maxAmountOfUsagesPerAccount:
								discountAction.usageLimitation.maxAmountOfUsagesPerAccount?.max,
							onlyForProductIds:
								discountAction.usageLimitation.onlyForProductIds?.map((l) => l) ??
								[],
							onlyForAccountIds:
								discountAction.usageLimitation.onlyForAccountIds?.map((l) => l) ??
								[],
					  }
					: undefined,
		},
		validationSchema: discountActionFormValidationSchema({ isArchived, kind, oldUsage }),
		onSubmit: (values, { setSubmitting }) => {
			const payload = {
				variables: {
					input: {
						discountActionId: discountAction.id,
						newTitle: values.title,
						newDiscountTypeKind: values.discountType as DiscountTypeKindInput,
						newPercentage: values.percentage,
						newValidUntil: values.validUntil,
						newUsageLimitation: values.usageLimitation,
						newIsReductible: values.isReductible,
					},
				},
				onCompleted: () => {
					toast.current?.show({
						severity: "success",
						life: 3000,
						summary: t("discounts.discountAction"),
						detail: isArchived
							? t("discounts.successReactivated")
							: t("discounts.successSaved"),
					});
				},
			};
			isArchived ? reactivateDiscountAction(payload) : editPercentageDiscountAction(payload);
			setSubmitting(false);
		},
		enableReinitialize: true,
	});

	return (
		<Card>
			<Toast ref={toast} />
			<Suspense fallback={<ProgressSpinner />}>
				<form onSubmit={formik.handleSubmit} className="p-fluid">
					<div className="flex flex-column">
						<ValidatedField<DiscountActionFormState, string>
							name="title"
							label={t("general.title")}
							required={true}
							component={DefaultTextFieldComponent}
							formikConfig={formik}
						/>
						<ValidatedField<DiscountActionFormState, boolean>
							name="isReductible"
							label={t("discounts.isReductible")}
							component={({ fieldValue, fieldName, updateField }) => (
								<DefaultSwitchComponent
									fieldValue={fieldValue}
									updateField={updateField}
									fieldName={fieldName ?? "isReductible"}
									required={false}
									isValid={true}
									disabled={false}
								/>
							)}
							formikConfig={formik}
						/>
						<ValidatedField<DiscountActionFormState, string>
							name="discountType"
							label={t("discounts.discountType")}
							component={({ fieldValue, updateField }) => (
								<DiscountTypeInput
									fieldValue={fieldValue}
									updateField={updateField}
								/>
							)}
							formikConfig={formik}
						/>
						<ValidatedField<DiscountActionFormState, number>
							name="percentage"
							label={t("discounts.percentageValue")}
							component={(props) => <DefaultNumberFieldComponent {...props} />}
							formikConfig={formik}
						/>
						<ValidatedField<DiscountActionFormState, string>
							name="validUntil"
							label={t("discounts.validUntil")}
							component={({ fieldName, fieldValue, updateField }) => (
								<Calendar
									name={fieldName}
									dateFormat="dd.mm.yy"
									showTime
									showButtonBar
									value={
										fieldValue
											? moment(fieldValue.replace("[UTC]", "")).toDate()
											: undefined
									}
									onChange={(e) => {
										updateField(
											e.value
												? moment(e.value as Date).toISOString(true)
												: undefined,
										);
									}}
								/>
							)}
							formikConfig={formik}
						/>
						<Card>
							<ValidatedField<DiscountActionFormState, UsageLimitedInput>
								name="usageLimitation"
								label={t("discounts.usageLimitations")}
								component={({ fieldValue, updateField }) => (
									<UsageLimitationInput
										fieldValue={fieldValue}
										update={updateField}
										initialValues={formik.initialValues.usageLimitation}
									/>
								)}
								formikConfig={formik}
							/>
						</Card>
						<Button
							type={ButtonType.Submit}
							icon={EpicIcons.SAVE}
							label={isArchived ? t("actions.reactivate") : t("actions.save")}
							disabled={formik.isSubmitting}
							loading={
								isEditingPercentageDiscountAction || isReactivatingDiscountAction
							}
						/>
					</div>
				</form>
			</Suspense>
		</Card>
	);
};
